import React from 'react';
import { List, Avatar, Badge, Typography, Row, Col, Tooltip } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { formatTime, getStatusColor } from '../conversationUtils';
import { useTheme } from '../../../Shared/GlobalContext/ThemeContext';

const { Text } = Typography;

// Typing animation keyframes
const typingAnimationStyle = `
  @keyframes typingAnimation {
    0% { transform: translateY(0); }
    50% { transform: translateY(-5px); }
    100% { transform: translateY(0); }
  }
`;

// Typing indicator component with local styles
const TypingIndicator = () => {
    const dotStyle = {
        width: '6px',
        height: '6px',
        borderRadius: '50%',
        backgroundColor: '#1890ff',
        display: 'inline-block',
        margin: '0 2px',
    };

    return (
        <div style={{ 
            display: 'inline-flex',
            alignItems: 'center',
            gap: '3px',
            marginRight: '5px'
        }}>
            <style dangerouslySetInnerHTML={{ __html: typingAnimationStyle }} />
            <div style={{ 
                ...dotStyle, 
                animation: 'typingAnimation 1s infinite ease-in-out',
                animationDelay: '0s'
            }} />
            <div style={{ 
                ...dotStyle, 
                animation: 'typingAnimation 1s infinite ease-in-out',
                animationDelay: '0.2s'
            }} />
            <div style={{ 
                ...dotStyle, 
                animation: 'typingAnimation 1s infinite ease-in-out',
                animationDelay: '0.4s'
            }} />
        </div>
    );
};

const ConversationListItem = React.memo(({
    conversation,
    selectedId,
    onSelectConversation,
    index,
    hoveredIndex,
    setHoveredIndex
}) => {
    const { isDarkMode } = useTheme();
    const { t } = useTranslation();
    const { contact, last_message, status, updated_at, id, is_writing } = conversation;

    const themeColors = {
        background: isDarkMode ? '#1f1f1f' : '#fff',
        selectedBackground: isDarkMode ? '#183a61' : '#e6f7ff',
        hoverBackground: isDarkMode ? '#2a2a2a' : '#f5f5f5',
        text: isDarkMode ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.88)',
        secondaryText: isDarkMode ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)',
        typingColor: '#1890ff',
    };

    return (
        <List.Item
            style={{
                cursor: 'pointer',
                background: selectedId === id ? themeColors.selectedBackground : themeColors.background,
                padding: '8px 16px',
                position: 'relative',
                transition: 'background-color 0.3s ease',
                ':hover': {
                    background: themeColors.hoverBackground
                }
            }}
            onClick={() => onSelectConversation(conversation)}
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
        >
            <List.Item.Meta
                avatar={
                    <Badge status={getStatusColor(status)}>
                        <Avatar
                            src={contact?.picture_url}
                            icon={!contact?.picture_url && <UserOutlined />}
                        />
                    </Badge>
                }
                title={
                    <Row justify="space-between" align="middle" style={{ width: '100%' }}>
                        <Col flex="auto" style={{ overflow: 'hidden', marginRight: 8 }}>
                            <Tooltip title={`${contact?.first_name || ''} ${contact?.last_name || ''}`.trim() || t('unknown_contact')}>
                                <Text strong style={{ color: themeColors.text }} ellipsis>
                                    {`${contact?.first_name || ''} ${contact?.last_name || ''}`.trim() || t('unknown_contact')}
                                </Text>
                            </Tooltip>
                        </Col>
                        <Col flex="none">
                            <Text type="secondary" style={{ fontSize: 12, color: themeColors.secondaryText, whiteSpace: 'nowrap' }}>
                                {formatTime(updated_at)}
                            </Text>
                        </Col>
                    </Row>
                }
                description={
                    <div>
                        {is_writing ? (
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <TypingIndicator />
                                <Text type="secondary" style={{ fontSize: 14, color: themeColors.typingColor }}>
                                    {t('agent_is_typing')}
                                </Text>
                            </div>
                        ) : (
                            <Text type="secondary" ellipsis style={{ fontSize: 14, color: themeColors.secondaryText }}>
                                {last_message?.content || t('no_messages_yet')}
                            </Text>
                        )}
                    </div>
                }
            />
        </List.Item>
    );
});

export default ConversationListItem;
